import React from "react";
import { Link } from "react-router-dom";
import { FiChevronRight } from "react-icons/fi";

/**
 * PageHeader - Consistent header for all pages
 *
 * @param {string} title - Page title (required)
 * @param {array} breadcrumb - Array of {label, to} objects for breadcrumb trail
 * @param {node} filters - React node for filter controls (center)
 * @param {node} actions - React node for action buttons (right side)
 * @param {boolean} sticky - Make header sticky on scroll
 * @param {boolean} compact - Use compact variant for sub-sections
 * @param {boolean} v4 - Use V4 styling variant
 * @param {node} icon - Icon for V4 variant badge
 * @param {string} subtitle - Subtitle text for V4 variant
 * @param {string} metadata - Additional metadata for V4 variant
 */
const PageHeader = ({
	title,
	breadcrumb = [],
	filters,
	actions,
	sticky = false,
	compact = false,
	v4 = false,
	icon,
	subtitle,
	metadata
}) => {
	const classNames = [
		"page-header",
		sticky && "page-header--sticky",
		compact && "page-header--compact",
		v4 && "page-header--v4",
	].filter(Boolean).join(" ");

	// V4 variant - ContentHeader style
	if (v4) {
		return (
			<header className={classNames}>
				<div className="page-header__left">
					{icon && <div className="page-header__icon">{icon}</div>}
					<div className="page-header__text">
						<h1 className="page-header__title">{title}</h1>
						{subtitle && <p className="page-header__subtitle">{subtitle}</p>}
						{metadata && <p className="page-header__metadata">{metadata}</p>}
					</div>
				</div>
				{actions && (
					<div className="page-header__right">
						<div className="page-header__actions">
							{actions}
						</div>
					</div>
				)}
			</header>
		);
	}

	// Original variant with breadcrumb support
	return (
		<header className={classNames}>
			<div className="page-header__left">
				{breadcrumb.length > 0 ? (
					<nav className="page-header__breadcrumb" aria-label="Breadcrumb">
						{breadcrumb.map((item, index) => (
							<React.Fragment key={item.to || index}>
								{index > 0 && <span className="separator"><FiChevronRight /></span>}
								{item.to ? (
									<Link to={item.to}>{item.label}</Link>
								) : (
									<span className="current">{item.label}</span>
								)}
							</React.Fragment>
						))}
					</nav>
				) : (
					<h1 className="page-header__title">{title}</h1>
				)}
			</div>

			{filters && (
				<div className="page-header__filters">
					{filters}
				</div>
			)}

			{actions && (
				<div className="page-header__right">
					<div className="page-header__actions">
						{actions}
					</div>
				</div>
			)}
		</header>
	);
};

export default PageHeader;
